/*
  Lightweight Motion compatibility layer for the theme runtime.
  Covers the subset of Motion One APIs used by assets/theme.js, collection.js,
  and search.js while relying on native browser primitives.
*/
(function () {
  'use strict';

  if (window.Motion) return;

  const noop = () => {};
  const clamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);
  const toMs = (seconds) => Math.max(0, Number(seconds || 0) * 1000);
  const defaultDuration = 0.3;

  const ScrollOffset = {
    Enter: [[0, 1], [1, 1]],
    Exit: [[0, 0], [1, 0]],
    Any: [[1, 0], [0, 1]],
    All: [[0, 0], [1, 1]]
  };

  function asArray(target) {
    if (!target) return [];
    if (typeof target === 'string') return Array.from(document.querySelectorAll(target));
    if (target instanceof Element || target === window || target === document) return [target];
    if (target instanceof NodeList || target instanceof HTMLCollection || Array.isArray(target)) {
      return Array.from(target).filter(Boolean);
    }
    return [];
  }

  function easingToCss(easing) {
    if (Array.isArray(easing) && easing.length === 4) return `cubic-bezier(${easing.join(',')})`;
    if (typeof easing === 'string') return easing === 'none' ? 'linear' : easing;
    return 'ease';
  }

  function valueAt(value, index, fallback) {
    if (Array.isArray(value)) {
      return value[index] ?? value[value.length - 1] ?? fallback;
    }
    return value ?? fallback;
  }

  function unit(value) {
    return typeof value === 'number' ? `${value}px` : value;
  }

  function normalizeKeyframes(keyframes) {
    const normalized = {};
    const x = keyframes.x;
    const y = keyframes.y;

    Object.keys(keyframes).forEach((property) => {
      if (property === 'x' || property === 'y') return;
      normalized[property] = keyframes[property];
    });

    if (x !== undefined || y !== undefined) {
      const length = Math.max(Array.isArray(x) ? x.length : 1, Array.isArray(y) ? y.length : 1);
      normalized.transform = Array.from({ length }, (_, index) => {
        const tx = unit(valueAt(x, index, 0));
        const ty = unit(valueAt(y, index, 0));
        return `translate(${tx}, ${ty})`;
      });
    }

    return normalized;
  }

  function applyFinalStyle(element, keyframes, index) {
    Object.keys(keyframes).forEach((property) => {
      const value = valueAt(keyframes[property], index, keyframes[property]);
      if (value === undefined || value === null) return;
      element.style[property] = value;
    });
  }

  function createControls(animations, duration) {
    const activeAnimations = animations.filter(Boolean);
    const controls = {
      animations: activeAnimations,
      duration,
      finished: Promise.all(activeAnimations.map((animation) => animation.finished).filter(Boolean)).catch(noop),
      stop() {
        activeAnimations.forEach((animation) => {
          if (typeof animation.cancel === 'function') animation.cancel();
        });
      },
      pause() {
        activeAnimations.forEach((animation) => {
          if (typeof animation.pause === 'function') animation.pause();
        });
      },
      play() {
        activeAnimations.forEach((animation) => {
          if (typeof animation.play === 'function') animation.play();
        });
      },
      forEachNative(callback) {
        activeAnimations.forEach((animation) => callback(animation, controls));
      }
    };

    Object.defineProperty(controls, 'currentTime', {
      get() {
        const first = activeAnimations[0];
        return first ? (first.currentTime || 0) / 1000 : 0;
      },
      set(seconds) {
        activeAnimations.forEach((animation) => {
          animation.currentTime = toMs(seconds);
        });
      }
    });

    Object.defineProperty(controls, 'playState', {
      get() {
        return activeAnimations[0]?.playState;
      }
    });

    return controls;
  }

  function animateProgress(callback, options = {}) {
    const duration = Number(options.duration ?? defaultDuration);
    const delay = Number(options.delay || 0);
    let frameId;
    let startTime;
    let stopped = false;

    const finished = new Promise((resolve) => {
      const tick = (time) => {
        if (stopped) return;
        if (startTime === undefined) startTime = time + toMs(delay);
        const progress = duration === 0 ? 1 : clamp((time - startTime) / toMs(duration));
        callback(progress);
        if (progress < 1) frameId = requestAnimationFrame(tick);
        else resolve();
      };

      frameId = requestAnimationFrame(tick);
    });

    return {
      duration,
      finished,
      stop() {
        stopped = true;
        cancelAnimationFrame(frameId);
      }
    };
  }

  function animate(target, keyframes, options = {}) {
    if (typeof target === 'function') return animateProgress(target, keyframes || {});

    const elements = asArray(target);
    const normalized = normalizeKeyframes(keyframes || {});
    const duration = Number(options.duration ?? defaultDuration);
    const delay = typeof options.delay === 'function' ? 0 : Number(options.delay || 0);

    if (duration === 0 && delay === 0) {
      elements.forEach((element) => applyFinalStyle(element, normalized, -1));
      return createControls([], duration);
    }

    const animations = elements.map((element, index) => {
      if (typeof element.animate !== 'function') {
        applyFinalStyle(element, normalized, -1);
        return null;
      }

      const elementOptions = {
        duration: toMs(duration),
        delay: toMs(typeof options.delay === 'function' ? options.delay(index, elements.length) : delay),
        easing: easingToCss(options.easing),
        iterations: options.repeat === Infinity ? Infinity : Number(options.repeat || 0) + 1,
        direction: options.direction || 'normal',
        fill: 'both'
      };

      const animation = element.animate(normalized, elementOptions);
      animation.finished.then(() => applyFinalStyle(element, normalized, -1)).catch(noop);
      return animation;
    });

    return createControls(animations, duration);
  }

  function timeline(sequence, options = {}) {
    const animations = [];
    let cursor = 0;

    sequence.forEach((step) => {
      if (!Array.isArray(step)) return;
      const [target, keyframes, stepOptions = {}] = step;
      const localOptions = Object.assign({}, options.defaultOptions || {}, stepOptions);
      const duration = Number(localOptions.duration ?? defaultDuration);
      const at = localOptions.at;

      if (typeof at === 'number') cursor = at;
      if (at === '<') cursor = Math.max(0, cursor - duration);

      localOptions.delay = Number(localOptions.delay || 0) + cursor;
      const controls = animate(target, keyframes, localOptions);
      animations.push(...controls.animations);
      cursor += duration;
    });

    return createControls(animations, cursor);
  }

  function inView(target, callback, options = {}) {
    const elements = asArray(target);
    if (!elements.length) return noop;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => callback({ target: element, isIntersecting: true }));
      return noop;
    }

    const leaveCallbacks = new WeakMap();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const onLeave = callback(entry);
          if (typeof onLeave === 'function') {
            leaveCallbacks.set(entry.target, onLeave);
          } else {
            observer.unobserve(entry.target);
          }
        } else if (leaveCallbacks.has(entry.target)) {
          leaveCallbacks.get(entry.target)(entry);
          leaveCallbacks.delete(entry.target);
        }
      });
    }, {
      root: options.root,
      rootMargin: options.margin,
      threshold: typeof options.amount === 'number' ? options.amount : 0
    });

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }

  // Document top of an element from layout, ignoring transforms (the element being
  // animated is often the scroll target itself, e.g. footer parallax)
  function layoutTop(element) {
    let top = 0;
    for (let node = element; node instanceof HTMLElement; node = node.offsetParent) top += node.offsetTop;
    return top;
  }

  const isEnterOffset = (offset) => Array.isArray(offset) && JSON.stringify(offset) === JSON.stringify(ScrollOffset.Enter);

  function scroll(control, options = {}) {
    const target = options.target || document.documentElement;
    const container = options.container || window;
    let frameId;

    if (control && typeof control.pause === 'function') control.pause();

    if (window.theme?.config?.isTouch || window.theme?.config?.mqlSmall) {
      if (control && typeof control !== 'function') {
        control.currentTime = control.duration || 1;
      }
      return noop;
    }

    const update = () => {
      frameId = null;
      let progress;

      if (target instanceof HTMLElement && target !== document.documentElement && target !== document.body && isEnterOffset(options.offset)) {
        // ScrollOffset.Enter, as in Motion: 0 when the target's top reaches the viewport bottom, 1 when its bottom does
        const top = layoutTop(target) - (window.scrollY || document.documentElement.scrollTop || 0);
        progress = (window.innerHeight - top) / Math.max(1, target.offsetHeight);
      } else if (target instanceof Element && target !== document.documentElement && target !== document.body) {
        const rect = target.getBoundingClientRect();
        progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      } else {
        const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
        const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        progress = scrollTop / maxScroll;
      }

      progress = clamp(progress);

      if (typeof control === 'function') {
        control({ y: { progress }, x: { progress }, time: performance.now() });
      } else if (control) {
        control.currentTime = (control.duration || 1) * progress;
      }
    };

    const requestUpdate = () => {
      if (frameId) return;
      frameId = requestAnimationFrame(update);
    };

    const scrollTarget = container === document.documentElement ? window : container;
    scrollTarget.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    requestUpdate();

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      scrollTarget.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (control && typeof control.stop === 'function') control.stop();
    };
  }

  function stagger(interval = 0.1, options = {}) {
    return (index, total) => {
      const from = options.from === 'last' ? total - 1 : options.from === 'center' ? (total - 1) / 2 : Number(options.from || 0);
      return Number(options.start || 0) + Math.abs(index - from) * interval;
    };
  }

  window.Motion = {
    ScrollOffset,
    animate,
    timeline,
    inView,
    scroll,
    stagger,
    spring() {
      return 'ease-out';
    }
  };
})();
